if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }

      if (this.optionObserver) {
        this.optionObserver.disconnect();
      }

      if (this.boundImmediateGroup) {
        this.section.removeEventListener('change', this.boundImmediateGroup);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.variantGrouping = this.mediaGroupingEnabled
        && this.getMediaGroupData().mode === 'variant';
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.variantGrouping) {
        // Single product with real options (e.g. Frame + Colour). Group images
        // per variant, hide colours with no variant for the selected frame.
        this.hideUnavailableColours();
        this.ensureValidColourSelected();
        this.observeOptionChanges();
        // Update the gallery AND hide invalid colours the instant an option is
        // clicked, using on-page variant data, instead of waiting for the
        // post-fetch on:variant:change event. Hiding colours only on the delayed
        // event left every colour visible for ~1s (the flicker) before filtering.
        this.boundImmediateGroup = () => {
          this.hideUnavailableColours();
          this.updateGroupFromSelection();
        };
        this.section.addEventListener('change', this.boundImmediateGroup);
        const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
        if (currentItem) this.setActiveMediaGroup(currentItem.dataset.mediaId, true);
      } else if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.hideUnavailableGroupOptions();
        const selectedName = this.getMediaGroupFromOptionSelectors();
        // On arrival at this product (page load or combined-listing frame
        // switch) the carried-over colour may not exist here. If so, snap the
        // colour selector to the base colour. This runs ONLY here, never on a
        // normal colour change, so selecting a valid colour is never reverted.
        if (selectedName && !this.getMediaGroupMap().groups[selectedName]) {
          this.selectGroupOption(Object.keys(this.getMediaGroupMap().groups)[0]);
        }
        this.setActiveMediaGroup(selectedName, true);
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

      if (this.classList.contains('media-gallery--thumbs-left')) {
        this.initThumbNav();
      }
    }

    /**
     * Up/down scroll arrows for the vertical (left) thumbnail strip. Shows the
     * arrows only when the strip overflows, and only the relevant direction.
     */
    initThumbNav() {
      this.thumbsScroller = this.querySelector('.media-thumbs');
      this.navUp = this.querySelector('.media-thumbs__nav--up');
      this.navDown = this.querySelector('.media-thumbs__nav--down');
      if (!this.thumbsScroller || !this.navUp || !this.navDown) return;

      const step = () => Math.max(120, this.thumbsScroller.clientHeight * 0.8);
      this.navUp.addEventListener('click', () => this.thumbsScroller.scrollBy({ top: -step(), behavior: 'smooth' }));
      this.navDown.addEventListener('click', () => this.thumbsScroller.scrollBy({ top: step(), behavior: 'smooth' }));

      this.updateThumbNav = () => {
        const el = this.thumbsScroller;
        const scrollable = el.scrollHeight - el.clientHeight > 2;
        this.navUp.hidden = !scrollable || el.scrollTop <= 2;
        this.navDown.hidden = !scrollable || el.scrollTop >= el.scrollHeight - el.clientHeight - 2;
      };
      this.thumbsScroller.addEventListener('scroll', this.updateThumbNav);
      window.addEventListener('on:debounced-resize', this.updateThumbNav);
      setTimeout(this.updateThumbNav, 150);
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.variantGrouping) {
        // Refresh which colours are valid for the (possibly new) frame, and if
        // the current colour no longer exists for it, move to a valid one.
        this.hideUnavailableColours();
        this.ensureValidColourSelected();
        const variant = evt.detail.variant;
        if (variant && variant.featured_media) {
          this.setActiveMediaGroup(String(variant.featured_media.id));
        }
      } else if (this.mediaGroupingEnabled) {
        this.hideUnavailableGroupOptions();
        // Colour change within the current product: show the group, but never
        // auto-revert the selection (no base-colour fallback select here).
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        if (variantMedia) {
          this.customSetActiveMedia(variantMedia, true);
        }
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Hides option values (e.g. colours) that have no media group on the
     * current product. In a combined listing each frame is a separate product
     * with its own colours; the option selector still lists the full union of
     * colours, so colours belonging only to the other frame would otherwise
     * remain selectable and produce an unavailable variant. A colour has a
     * media group only when that colour exists (and has a lead image) on this
     * product, so group membership is used as the source of truth.
     */
    hideUnavailableGroupOptions() {
      if (!this.mediaGroupingEnabled) return;

      const groupData = this.getMediaGroupData();
      if (!groupData) return;

      const validNames = Object.keys(this.getMediaGroupMap().groups);
      // Safety: if no groups resolved, do nothing rather than hide everything.
      if (validNames.length === 0) return;

      const optionSelectors = this.section.querySelectorAll('.option-selector');
      const selector = optionSelectors[groupData.groupOptionIndex];
      if (!selector) return;

      selector.querySelectorAll('.js-option').forEach((input) => {
        const hide = !validNames.includes(input.value);
        input.classList.toggle('media-group-option-hidden', hide);
        const label = selector.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (label) label.classList.toggle('media-group-option-hidden', hide);
      });
    }

    /**
     * Selects an option value (by name) in the grouping option selector.
     * Used to fall back to a base colour when the active selection isn't
     * available on the current product (combined-listing frame switch).
     * Guarded so it never re-fires once the base colour is selected.
     * @param {string} groupName - Option value to select.
     */
    selectGroupOption(groupName) {
      if (!groupName) return;

      const groupData = this.getMediaGroupData();
      if (!groupData) return;

      const selector = this.section.querySelectorAll('.option-selector')[groupData.groupOptionIndex];
      if (!selector) return;

      const target = selector.querySelector(`.js-option[value="${CSS.escape(groupName)}"]`);
      if (!target || target.matches(':checked, [aria-selected="true"]')) return;

      setTimeout(() => {
        const customSelect = target.closest('custom-select');
        if (customSelect) {
          customSelect.selectOption(target);
        } else {
          target.click();
        }
      }, 0);
    }

    /**
     * Reads the currently selected option values, in option order.
     * @returns {Array} Selected value per option selector (null if none).
     */
    getSelectedOptionValues() {
      const selected = [];
      this.section.querySelectorAll('.option-selector').forEach((sel) => {
        if (sel.dataset.selectorType === 'dropdown') {
          const opt = sel.querySelector('.custom-select__option[aria-selected="true"]');
          selected.push(opt && opt.dataset.value !== '' ? opt.dataset.value : null);
        } else {
          const input = sel.querySelector('input:checked');
          selected.push(input ? input.value : null);
        }
      });
      return selected;
    }

    /**
     * Variant mode: hide grouping-option values (colours) that have no variant
     * for the currently selected other options (e.g. the frame). Sold-out but
     * existing combos are left visible so native availability can grey them.
     */
    hideUnavailableColours() {
      const data = this.getMediaGroupData();
      if (!data || data.mode !== 'variant' || !Array.isArray(data.variants)) return;

      const groupIdx = data.groupOptionIndex;
      const selector = this.section.querySelectorAll('.option-selector')[groupIdx];
      if (!selector) return;

      const selected = this.getSelectedOptionValues();

      selector.querySelectorAll('.js-option').forEach((input) => {
        const value = input.value || input.dataset.value;
        const exists = data.variants.some((variant) => (
          variant.options[groupIdx] === value
          && variant.options.every(
            (optVal, i) => i === groupIdx || selected[i] == null || optVal === selected[i]
          )
        ));
        input.classList.toggle('media-group-option-hidden', !exists);
        const label = selector.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (label) label.classList.toggle('media-group-option-hidden', !exists);
      });
    }

    /**
     * Variant mode: resolve the selected variant from on-page data and show its
     * media group immediately (on the synchronous change event), so the gallery
     * switches in the same frame as the click rather than after the fetch.
     */
    updateGroupFromSelection() {
      const data = this.getMediaGroupData();
      if (!data || data.mode !== 'variant' || !Array.isArray(data.variants)) return;

      const selected = this.getSelectedOptionValues();
      if (selected.indexOf(null) !== -1) return;

      const variant = data.variants.find(
        (v) => v.options.length === selected.length
          && v.options.every((opt, i) => opt === selected[i])
      );
      if (!variant) return;

      const mediaId = data.variantFeaturedMedia[String(variant.id)];
      if (mediaId) this.setActiveMediaGroup(String(mediaId));
    }

    /**
     * Variant mode: re-apply colour hiding the moment the variant picker swaps
     * in fresh swatch markup (which the server renders without the hidden
     * markers). MutationObserver callbacks run before the browser paints, so
     * the invalid colours never flash into view during a variant change.
     */
    observeOptionChanges() {
      if (this.optionObserver) return;
      this.optionObserver = new MutationObserver(() => this.hideUnavailableColours());
      // Watch node additions/removals only (not attribute changes), so the
      // class toggles done by hideUnavailableColours can't retrigger this.
      this.optionObserver.observe(this.section, { childList: true, subtree: true });
    }

    /**
     * Variant mode: if the selected colour has just become invalid for the
     * selected frame (now hidden), move selection to the first valid colour.
     * Guarded so it only fires while the current colour is invalid.
     */
    ensureValidColourSelected() {
      const data = this.getMediaGroupData();
      if (!data || data.mode !== 'variant') return;

      const selector = this.section.querySelectorAll('.option-selector')[data.groupOptionIndex];
      if (!selector) return;

      const checked = selector.querySelector(
        'input:checked, .custom-select__option[aria-selected="true"]'
      );
      if (!checked || !checked.classList.contains('media-group-option-hidden')) return;

      const firstValid = selector.querySelector('.js-option:not(.media-group-option-hidden)');
      if (!firstValid || firstValid === checked) return;

      setTimeout(() => {
        const customSelect = firstValid.closest('custom-select');
        if (customSelect) {
          customSelect.selectOption(firstValid);
        } else {
          firstValid.click();
        }
      }, 0);
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        try {
          this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
        } catch (err) {
          // Never let malformed grouping data break the whole gallery.
          this.variantMediaData = false;
        }
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        this.viewerItems = this.querySelectorAll('.media-viewer__item');

        const addToGroup = (key, item) => {
          if (!this.mediaGroupMap.groups[key]) {
            this.mediaGroupMap.groups[key] = { name: key, items: [] };
          }
          const groupItem = { main: item };
          if (this.thumbs) {
            groupItem.thumb = this.thumbs.querySelector(
              `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
            );
          }
          this.mediaGroupMap.groups[key].items.push(groupItem);
        };

        if (variantMediaData.mode === 'variant') {
          // Variant mode: a new group starts at each media that is some
          // variant's featured image; following media join that group. Group
          // key is the lead media id, so two variants that share an image (e.g.
          // sizes of one colour) share a group, while the same colour on two
          // frames stays distinct.
          const leaderIds = new Set(
            Object.values(variantMediaData.variantFeaturedMedia).map((id) => parseInt(id, 10))
          );
          let currentKey = false;
          this.viewerItems.forEach((item) => {
            const mediaId = parseInt(item.dataset.mediaId, 10);
            if (leaderIds.has(mediaId)) currentKey = String(mediaId);
            if (currentKey) addToGroup(currentKey, item);
          });
        } else {
          // Legacy option (colour-name) mode.
          let currentMediaOptionName = false;
          this.viewerItems.forEach((item) => {
            for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
              if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
                if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                  currentMediaOptionName = variantMediaData.variantMedia[i].option;
                }
              }
            }
            if (currentMediaOptionName) addToGroup(currentMediaOptionName, item);
          });
        }

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName, fallbackToBase = false) {
      const mediaGroupMap = this.getMediaGroupMap();
      let selectedGroup;

      if (groupName) {
        selectedGroup = mediaGroupMap.groups[groupName];
        if (!selectedGroup) {
          // The selected option has no media group on this product (e.g. a
          // colour that only exists on the other frame in a combined listing).
          if (fallbackToBase) {
            // On arrival at the product, show the base colour rather than
            // every colour. Selecting the base colour in the picker is handled
            // by the caller (init), so this only fixes the gallery display.
            selectedGroup = mediaGroupMap.groups[Object.keys(mediaGroupMap.groups)[0]];
          }
          // On a colour change, keep the current images (don't bleed all
          // colours and don't revert the selection).
          if (!selectedGroup) return;
        }
      } else {
        selectedGroup = mediaGroupMap.groupFromItem(this.currentItem);
      }

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
